import React from 'react';
import { Link } from 'react-router-dom';
import { Star, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { TESTIMONIALS } from '../mock';

export const Testimonials = () => {
  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-captiva-cream to-captiva-cream-dark">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-1 mb-6">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-8 w-8 fill-captiva-gold text-captiva-gold" />
            ))}
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Avis Clients
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Des centaines de clients satisfaits à travers toute la Belgique
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-captiva-gold mb-2">500+</p>
              <p className="text-gray-600">Événements réussis</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-captiva-gold mb-2">5.0</p>
              <p className="text-gray-600">Note moyenne</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-captiva-gold mb-2">98%</p>
              <p className="text-gray-600">Clients satisfaits</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-captiva-gold mb-2">100%</p>
              <p className="text-gray-600">Avis vérifiés</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {TESTIMONIALS.map((testimonial) => (
              <Card
                key={testimonial.id}
                className="p-6 hover:shadow-2xl transition-all duration-300 border-2 hover:border-captiva-gold-light group"
              >
                {/* Rating */}
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-captiva-gold text-captiva-gold" />
                  ))}
                </div>

                {/* Comment */}
                <p className="text-gray-700 mb-6 leading-relaxed text-base">
                  "{testimonial.comment}"
                </p>

                {/* Author Info */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.event}</p>
                  </div>
                  {testimonial.verified && (
                    <div className="flex items-center space-x-1 text-captiva-gold">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="text-xs font-medium">Vérifié</span>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Video Testimonials Section (Placeholder) */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pourquoi choisir Captiva ?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-captiva-cream rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-8 w-8 text-captiva-gold" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Service professionnel</h3>
              <p className="text-gray-600 leading-relaxed">
                Équipe expérimentée, matériel haut de gamme et support technique garanti
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-captiva-cream rounded-full flex items-center justify-center mx-auto mb-6">
                <Star className="h-8 w-8 text-captiva-gold fill-captiva-gold" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Expérience premium</h3>
              <p className="text-gray-600 leading-relaxed">
                Photos de qualité professionnelle et animations interactives captivantes
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-captiva-cream rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-8 w-8 text-captiva-gold" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">100% satisfait</h3>
              <p className="text-gray-600 leading-relaxed">
                Des centaines d'événements réussis et des clients qui nous recommandent
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-captiva-beige to-captiva-gold-dark text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Rejoignez nos clients satisfaits
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Faites de votre événement un moment inoubliable avec Captiva
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-white text-captiva-gold-dark hover:bg-gray-100 px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Réserver maintenant
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/tarifs">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-captiva-gold-dark px-8 py-6 text-lg transition-all duration-300"
              >
                Voir les tarifs
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;
